<script setup>
import { computed, ref, watch } from 'vue'
import { useAccessStore } from '@/stores/access'
import moment from 'moment'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Switch } from '@/components/ui/switch'

const props = defineProps({
  open: { type: Boolean, default: false },
  link: { type: Object, default: null }, // null = create
})
const emit = defineEmits(['update:open', 'submit'])

const accessStore = useAccessStore()
const roles = computed(() => accessStore.roleChoices('project'))
const defaultRoleId = computed(() => accessStore.defaultJoinRoleId ?? roles.value[0]?.value ?? null)

const EXPIRY_PRESETS = [
  { value: 'never', label: 'Never' },
  { value: '1h', label: '1 hour' },
  { value: '1d', label: '1 day' },
  { value: '1w', label: '1 week' },
]
const PRESET_MS = { '1h': 3600000, '1d': 86400000, '1w': 604800000 }
const USES_PRESETS = [
  { value: 'unlimited', label: 'Unlimited' },
  { value: '5', label: '5' },
  { value: '25', label: '25' },
  { value: '100', label: '100' },
]

const name = ref('')
const roleId = ref(null)
const expiryPreset = ref('never')
const usesPreset = ref('unlimited')
const requiresApproval = ref(false)

const isEdit = computed(() => !!props.link)

watch(
  () => props.open,
  (open) => {
    if (!open) return
    name.value = props.link?.name || ''
    roleId.value = props.link?.role_id ?? defaultRoleId.value
    // An existing expiry stays as it is until a preset is picked.
    expiryPreset.value = props.link?.expires_at ? 'keep' : 'never'
    const uses = props.link?.max_uses
    usesPreset.value = !uses ? 'unlimited' : USES_PRESETS.some((p) => p.value === String(uses)) ? String(uses) : 'keep'
    requiresApproval.value = !!props.link?.requires_approval
  },
  { immediate: true }
)

const maxUses = computed(() => {
  if (usesPreset.value === 'unlimited') return null
  if (usesPreset.value === 'keep') return props.link?.max_uses || null
  return parseInt(usesPreset.value, 10)
})

const expiresAt = computed(() => {
  if (expiryPreset.value === 'never') return null
  if (expiryPreset.value === 'keep') return props.link?.expires_at || null
  return new Date(Date.now() + PRESET_MS[expiryPreset.value]).toISOString()
})

const submit = () => {
  emit('submit', {
    name: name.value.trim(),
    role_id: roleId.value,
    expires_at: expiresAt.value,
    max_uses: maxUses.value,
    requires_approval: requiresApproval.value,
  })
}
</script>

<template>
  <Dialog :open="open" @update:open="emit('update:open', $event)">
    <DialogContent class="max-w-[345px]">
      <DialogHeader>
        <DialogTitle>{{ isEdit ? 'Edit join link' : 'Create join link' }}</DialogTitle>
        <DialogDescription class="sr-only">Anyone with the link can join the project.</DialogDescription>
      </DialogHeader>

      <form class="space-y-4" @submit.prevent="submit">
        <div class="space-y-1.5">
          <Label for="join-link-name">Name</Label>
          <Input id="join-link-name" v-model="name" placeholder="e.g. Field team (optional)" maxlength="60" />
        </div>

        <div class="space-y-1.5">
          <Label for="join-link-role">Role</Label>
          <Select :model-value="roleId == null ? undefined : String(roleId)" @update:model-value="roleId = Number($event)">
            <SelectTrigger id="join-link-role">
              <SelectValue placeholder="Select a role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem v-for="r in roles" :key="r.value" :value="String(r.value)">{{ r.label }}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div class="space-y-1.5">
          <Label>Max uses</Label>
          <div class="flex items-center gap-2">
            <Tabs :model-value="usesPreset" @update:model-value="usesPreset = String($event)">
              <TabsList class="h-9" data-testid="join-link-uses">
                <TabsTrigger v-for="p in USES_PRESETS" :key="p.value" :value="p.value" class="h-7 px-3 text-xs">
                  {{ p.label }}
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <span v-if="usesPreset === 'keep'" class="text-xs text-muted-foreground" data-testid="join-link-uses-current">
              {{ link.max_uses }} uses
            </span>
          </div>
        </div>

        <div class="space-y-1.5">
          <Label>Expires</Label>
          <div class="flex items-center gap-2">
            <Tabs :model-value="expiryPreset" @update:model-value="expiryPreset = String($event)">
              <TabsList class="h-9" data-testid="join-link-expiry">
                <TabsTrigger v-for="p in EXPIRY_PRESETS" :key="p.value" :value="p.value" class="h-7 px-3 text-xs">
                  {{ p.label }}
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <span v-if="expiryPreset === 'keep'" class="text-xs text-muted-foreground" data-testid="join-link-expiry-current">
              Expires {{ moment(link.expires_at).format('D MMM, HH:mm') }}
            </span>
          </div>
        </div>

        <div class="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
          <div class="min-w-0">
            <Label for="join-link-approval" class="cursor-pointer">Moderation</Label>
            <p class="mt-0.5 text-xs text-muted-foreground">New members join only after approve.</p>
          </div>
          <Switch id="join-link-approval" v-model="requiresApproval" data-testid="join-link-approval" />
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" @click="emit('update:open', false)">Cancel</Button>
          <Button type="submit" data-testid="join-link-submit">
            {{ isEdit ? 'Save' : 'Create' }}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
</template>
